import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReportDefinition } from "../catalog";

export function NewBadge() {
	const { t } = useTranslation();
	return (
		<span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success ring-1 ring-emerald-200">
			{t("reports.home.new")}
		</span>
	);
}

/** Live hint on a report row, e.g. "7 items" on Low stock. */
export interface RowTag {
	label: string;
	tone: "warning" | "info";
}

function Tag({ tag }: { tag: RowTag }) {
	return (
		<span
			className={cn(
				"shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
				tag.tone === "warning"
					? "bg-warning/10 text-warning ring-amber-200"
					: "bg-primary/5 text-primary ring-primary/20",
			)}
		>
			{tag.label}
		</span>
	);
}

function PinButton({ pinned, onToggle, title }: { pinned: boolean; onToggle: () => void; title: string }) {
	const { t } = useTranslation();
	const label = pinned ? t("reports.home.unpinNamed", { name: title }) : t("reports.home.pinNamed", { name: title });
	return (
		<button
			type="button"
			onClick={onToggle}
			aria-pressed={pinned}
			aria-label={label}
			title={label}
			className={cn(
				"flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-warning/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
				pinned ? "text-warning" : "text-muted-foreground/50 hover:text-warning",
			)}
		>
			<Star className={cn("h-4 w-4", pinned && "fill-amber-400")} aria-hidden="true" />
		</button>
	);
}

interface ReportRowProps {
	report: ReportDefinition;
	/** Query string carried into the report (the current filters). */
	search: string;
	pinned: boolean;
	onTogglePin: () => void;
	tag?: RowTag;
}

/** A report in the catalog: icon, name, one line of what it answers, live tag, and pin. */
export function ReportRow({ report, search, pinned, onTogglePin, tag }: ReportRowProps) {
	const { t } = useTranslation();
	const Icon = report.icon;
	const title = t(`reports.catalog.${report.key}.title`);
	return (
		<li className="group flex items-center gap-1 border-b border-border/70 last:border-0">
			<Link
				to={`/reports/${report.id}${search}`}
				className="flex min-w-0 flex-1 items-center gap-3 rounded-xl py-3 pe-1 ps-2 transition-[background-color,padding] duration-200 hover:bg-primary/5 hover:ps-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			>
				<span
					className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary"
					aria-hidden="true"
				>
					<Icon className="h-5 w-5" />
				</span>
				<span className="min-w-0 flex-1">
					<span className="flex flex-wrap items-center gap-2">
						<span className="font-medium text-foreground">{title}</span>
						{report.isNew && <NewBadge />}
					</span>
					<span className="mt-0.5 line-clamp-2 text-xs text-muted-foreground lg:line-clamp-1">
						{t(`reports.catalog.${report.key}.description`)}
					</span>
				</span>
				{tag && <Tag tag={tag} />}
				<ChevronRight
					className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary rtl:rotate-180 rtl:group-hover:-translate-x-0.5"
					aria-hidden="true"
				/>
			</Link>
			<PinButton pinned={pinned} onToggle={onTogglePin} title={title} />
		</li>
	);
}

/** Larger card for quick access (pinned or most used reports). */
export function ReportCard({ report, search, pinned, onTogglePin, tag }: ReportRowProps) {
	const { t } = useTranslation();
	const Icon = report.icon;
	const title = t(`reports.catalog.${report.key}.title`);
	return (
		<li className="relative flex rounded-2xl border bg-card shadow-sm transition-colors hover:border-primary/40">
			<Link
				to={`/reports/${report.id}${search}`}
				className="flex min-w-0 flex-1 items-start gap-3 rounded-2xl p-4 pe-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
			>
				<span
					className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/5 text-primary"
					aria-hidden="true"
				>
					<Icon className="h-5 w-5" />
				</span>
				<span className="min-w-0 flex-1">
					<span className="block text-xs text-muted-foreground">
						{t(`reports.groups.${report.group}.title`)}
					</span>
					<span className="mt-0.5 block font-semibold text-foreground">{title}</span>
					<span className="mt-1 line-clamp-2 block text-xs text-muted-foreground">
						{t(`reports.catalog.${report.key}.description`)}
					</span>
					{tag && (
						<span className="mt-2 inline-block">
							<Tag tag={tag} />
						</span>
					)}
				</span>
			</Link>
			<span className="absolute end-2 top-2">
				<PinButton pinned={pinned} onToggle={onTogglePin} title={title} />
			</span>
		</li>
	);
}
